import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingBagIcon, TrashIcon, ArrowLeftIcon, ArrowRightIcon, TruckIcon, ShieldCheckIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useCart } from '../context/CartContext'
import { getShippingInfo, UNITS_PER_BOX, getLinePrice } from '../data/products'
import BulkOffersBanner from '../components/BulkOffersBanner'
import api from '../config/api'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import StripePaymentForm from '../components/StripePaymentForm'
const isLocal = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1' || 
  window.location.hostname.endsWith('.local')
)

const stripePromise = loadStripe(
  isLocal 
    ? 'pk_test_51TUCRO1Kxdb9KjJFcarTlZkni0AJrv9GEnF9F0lnjHG30wixfrNXAuijtHCKaPYxKqovkmnqUS6vkk858cXHmtwt00iI4NSyV2'
    : 'pk_live_51TUCRO1Kxdb9KjJFfaZxyEqLj8XEQorVv3MAmz8qvFGDXDo0P3FPYTxAuJkLitQr5M27YBIarRSnxW2ld4hpAENi005cT6fK1K'
)

const steps = ['Cart Review', 'Shipping Details', 'Payment']

const Checkout = () => {
  const navigate = useNavigate()
  const { cartItems, removeFromCart, updateQuantity, subtotal, totalBoxes, clearCart } = useCart()
  const [step, setStep] = useState(0)
  const [orderError, setOrderError] = useState('')
  const [orderLoading, setOrderLoading] = useState(false)
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', province: 'Ontario', postalCode: '',
  })
  const [paymentInfo, setPaymentInfo] = useState({ card: '', expiry: '', cvv: '', name: '' })
  const hasSubscription = cartItems.some(item => !!item.subscription)
  const shipping = getShippingInfo(totalBoxes, hasSubscription)

  const [hasDiscount, setHasDiscount] = useState(() => localStorage.getItem('twiggle_claimed_first_order_discount') === 'true')

  useEffect(() => {
    const checkDiscount = () => {
      setHasDiscount(localStorage.getItem('twiggle_claimed_first_order_discount') === 'true')
    }
    window.addEventListener('storage', checkDiscount)
    checkDiscount()
    return () => window.removeEventListener('storage', checkDiscount)
  }, [])

  const discountAmount = hasDiscount ? subtotal * 0.10 : 0
  const finalTotal = Math.max(0, subtotal - discountAmount + shipping.cost)

  const handleShippingChange = e => setShippingInfo(p => ({ ...p, [e.target.name]: e.target.value }))
  const handlePaymentChange = e => setPaymentInfo(p => ({ ...p, [e.target.name]: e.target.value }))

  const handlePlaceOrder = async (e) => {
    e.preventDefault()
    setOrderLoading(true)
    setOrderError('')
    try {
      // Helper: convert File to Base64 string
      const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
      })

      // Extract only last 4 digits — never send full card number to server
      const cardLast4 = paymentInfo.card.replace(/\s/g, '').slice(-4)

      // Build cart items, converting any engraving File to Base64
      const builtCartItems = await Promise.all(cartItems.map(async (item) => {
        let engravingBase64 = null
        let engravingFileName = null
        if (item.engravingData?.file) {
          try {
            engravingBase64 = await toBase64(item.engravingData.file)
            engravingFileName = item.engravingData.file.name
          } catch (_) {}
        }
        return {
          name:             item.product.fullName,
          quantity:         item.quantity,
          pricePerBox:      item.product.pricePerBox,
          lineTotal:        item.product.pricePerBox * item.quantity,
          engravingImage:   engravingBase64,
          engravingFileName: engravingFileName,
          subscription:     item.subscription?.label || null,
        }
      }))

      await api.post('/checkout.php', {
        shipping: shippingInfo,
        cartItems: builtCartItems,
        subtotal:     subtotal - discountAmount,
        shippingCost: shipping.cost,
        total:        finalTotal,
        cardLast4,
      })
      clearCart()
      navigate('/order-confirmation')
    } catch (err) {
      setOrderError(err.message)
    } finally {
      setOrderLoading(false)
    }
  }

  if (cartItems.length === 0 && step === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-center px-4">
        <ShoppingBagIcon className="w-20 h-20 text-gray-200 mb-5" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h1>
        <p className="text-gray-500 text-sm mb-6">Add some eco-friendly toothbrushes first.</p>
        <button
          onClick={() => navigate('/shop')}
          className="px-8 py-3.5 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
        >
          Browse Products
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Header */}
        <div className="mb-8">
          <button onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/shop')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 transition-colors mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {step > 0 ? 'Back' : 'Continue Shopping'}
          </button>
          <h1 className="text-2xl font-extrabold text-gray-900">Checkout</h1>

          {/* Steps */}
          <div className="flex items-center gap-0 mt-5">
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    i < step ? 'bg-green-500 text-white' : i === step ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {i < step ? <CheckIcon className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-sm font-medium ${i === step ? 'text-gray-900' : 'text-gray-400'}`}>{s}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-3 ${i < step ? 'bg-green-400' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main Form Area */}
          <div className="lg:col-span-2 space-y-6">

            {/* Step 0: Cart Review */}
            {step === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="font-bold text-gray-900 text-lg">Review Your Order</h2>
                </div>
                <div className="divide-y divide-gray-50">
                  {cartItems.map((item, i) => (
                    <div key={i} className="p-6 flex gap-5">
                      <div className="w-20 h-20 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-gray-100">
                        <img src={item.product.image} alt={item.product.fullName} className="max-h-full max-w-full object-contain p-2" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{item.product.fullName}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{item.quantity * UNITS_PER_BOX} toothbrushes ({item.quantity} box{item.quantity > 1 ? 'es' : ''}){item.isFreeGift ? ' · FREE' : ''}</p>
                        {item.engravingData?.file && (
                          <p className="text-xs text-amber-600 mt-1">Custom engraving: {item.engravingData.file.name}</p>
                        )}
                        {item.subscription && (
                          <p className="text-xs text-green-600 mt-1">Subscription: {item.subscription.label}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateQuantity(i, item.quantity - 1)} disabled={item.quantity <= 1}
                              className="w-7 h-7 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 text-sm flex items-center justify-center disabled:opacity-40">−</button>
                            <span className="text-sm font-semibold w-5 text-center">{item.quantity}</span>
                            <button onClick={() => updateQuantity(i, item.quantity + 1)}
                              className="w-7 h-7 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 text-sm flex items-center justify-center">+</button>
                          </div>
                          <button onClick={() => removeFromCart(i)} className="text-gray-400 hover:text-red-500 transition-colors">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-gray-900">${getLinePrice(item).toFixed(2)}</p>
                        <p className="text-xs text-gray-400 mt-0.5">${item.product.pricePerBox}/box</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-6 border-t border-gray-100">
                  <button
                    id="checkout-next-shipping"
                    onClick={() => setStep(1)}
                    className="w-full py-3.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
                  >
                    Continue to Shipping
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Shipping Details */}
            {step === 1 && (
              <form onSubmit={(e) => { e.preventDefault(); setStep(2) }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
              >
                <div className="p-6 border-b border-gray-100 flex items-center gap-3">
                  <TruckIcon className="w-5 h-5 text-primary-600" />
                  <h2 className="font-bold text-gray-900 text-lg">Shipping Information</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700 font-medium">
                    Shipping available <strong>across all of Canada</strong>. $15.00 standard shipping for 1–4 boxes. <strong>Free shipping on orders of 5+ boxes!</strong>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { name: 'firstName', label: 'First Name', placeholder: 'Dr. Jane' },
                      { name: 'lastName', label: 'Last Name', placeholder: 'Smith' },
                    ].map(f => (
                      <div key={f.name}>
                        <label htmlFor={`ship-${f.name}`} className="block text-xs font-semibold text-gray-700 mb-1.5">{f.label} *</label>
                        <input id={`ship-${f.name}`} name={f.name} required value={shippingInfo[f.name]} onChange={handleShippingChange}
                          placeholder={f.placeholder}
                          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none" />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="ship-email" className="block text-xs font-semibold text-gray-700 mb-1.5">Email *</label>
                      <input id="ship-email" name="email" type="email" required value={shippingInfo.email} onChange={handleShippingChange}
                        placeholder="you@clinic.com"
                        className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none" />
                    </div>
                    <div>
                      <label htmlFor="ship-phone" className="block text-xs font-semibold text-gray-700 mb-1.5">Phone</label>
                      <input id="ship-phone" name="phone" type="tel" value={shippingInfo.phone} onChange={handleShippingChange}
                        placeholder="+1 (416) 555-0100"
                        className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="ship-address" className="block text-xs font-semibold text-gray-700 mb-1.5">Street Address *</label>
                    <input id="ship-address" name="address" required value={shippingInfo.address} onChange={handleShippingChange}
                      placeholder="123 Maple Street, Suite 200"
                      className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="ship-city" className="block text-xs font-semibold text-gray-700 mb-1.5">City *</label>
                      <input id="ship-city" name="city" required value={shippingInfo.city} onChange={handleShippingChange}
                        placeholder="Toronto"
                        className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none" />
                    </div>
                    <div>
                      <label htmlFor="ship-province" className="block text-xs font-semibold text-gray-700 mb-1.5">Province *</label>
                      <input id="ship-province" name="province" required value={shippingInfo.province} onChange={handleShippingChange}
                        placeholder="Ontario"
                        className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none" />
                    </div>
                    <div>
                      <label htmlFor="ship-postal" className="block text-xs font-semibold text-gray-700 mb-1.5">Postal Code *</label>
                      <input id="ship-postal" name="postalCode" required value={shippingInfo.postalCode} onChange={handleShippingChange}
                        placeholder="M5V 1A1"
                        className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none" />
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-100">
                  <button type="submit" id="checkout-next-payment"
                    className="w-full py-3.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors">
                    Continue to Payment
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Payment */}
            {step === 2 && (
              <Elements stripe={stripePromise}>
                <StripePaymentForm 
                  shippingInfo={shippingInfo}
                  cartItems={cartItems}
                  subtotal={subtotal - discountAmount}
                  shippingCost={shipping.cost}
                  total={finalTotal}
                  clearCart={clearCart}
                  navigate={navigate}
                />
              </Elements>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="space-y-4">
            <BulkOffersBanner />
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-24">
              <h3 className="font-bold text-gray-900 mb-4">Order Summary</h3>

              {/* Items */}
              <div className="space-y-3 mb-4">
                {cartItems.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-600 truncate pr-2">
                      {item.product.name} × {item.quantity}
                    </span>
                    <span className="font-medium text-gray-900 flex-shrink-0">
                      ${getLinePrice(item).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {hasDiscount && (
                  <div className="flex justify-between text-green-600 font-semibold">
                    <span>First Order Discount (-10%)</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className={shipping.cost === 0 ? 'text-green-600 font-medium' : ''}>
                    {shipping.cost === 0 ? 'Free' : `$${shipping.cost.toFixed(2)}`}
                  </span>
                </div>
                {shipping.cost > 0 && <p className="text-xs text-gray-400">{shipping.note}</p>}
              </div>

              <div className="border-t border-gray-100 mt-4 pt-4 flex justify-between font-extrabold text-gray-900 text-base">
                <span>Total</span>
                <span>${finalTotal.toFixed(2)} CAD</span>
              </div>

              <div className="mt-5 space-y-2 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="w-4 h-4 text-primary-500" />
                  Secure checkout
                </div>
                <div className="flex items-center gap-2">
                  <TruckIcon className="w-4 h-4 text-primary-500" />
                  Shipping across all of Canada
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checkout
